import React, { useState, useEffect, useRef } from "react";
import styles from "../../assets/containers/banner.module.css";
import selectCoursesIllustration from "../../../src/assets/images/select-courses-illustration.webp";
import { ChevronDownIcon } from "../../assets/icons/ChevronDown";
import { useAppGlobalConfig } from "../../providers/AppGlobalConfigContext";
import { getWizmeId } from "../../helpers/user";
import { getCompletenessPercentageByBookWithFallback } from "../../helpers/progressFallback";

const Banner = (props) => {
  const { isEmbedMode } = useAppGlobalConfig();
  const [tenant, setTenant] = useState();

  const {
    currentBook,
    courses,
    changeBook,
    currentLanguage,
    totalLessonsCount,
  } = props;
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(currentBook.name);
  const [percentage, setPercentage] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(0);
  const dropdownRef = useRef(null);
  let progressValue = percentage; // between 0 and 100
  let statusBarPhrase = "You haven't finished any lessons";

  useEffect(() => {
    isEmbedMode ? setTenant("cupheadless") : setTenant("cupfranchise");
  }, [isEmbedMode]);

  useEffect(() => {
    if (currentBook.id && tenant) {
      checkPercentage();
    }
  }, [currentBook.id, tenant, totalLessonsCount]);

  if (progressValue >= 1 && progressValue <= 99) {
    statusBarPhrase = `${completedLessons} lesson${completedLessons > 1 ? "s" : ""} completed`;
  } else if (progressValue === 100) {
    statusBarPhrase = "Congratulations! You have completed all your lessons!";
  }

  const sortedCourses = courses
    .filter((course) => course.book.language.code === currentLanguage)
    .sort((a, b) => a.book.name.localeCompare(b.book.name));

  const toggleDropdown = () => {
    setDropdownOpen(!isDropdownOpen);
  };

  const handleBookSelection = (book) => {
    setSelectedBook(book.name);
    changeBook({ target: { value: book.id } });
    setDropdownOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  const checkPercentage = async () => {
    try {
      const platformId = await getWizmeId();
      if (!platformId || !totalLessonsCount)
        throw new Error("Platform ID or total lessons count is missing");
      const result = await getCompletenessPercentageByBookWithFallback(
        tenant,
        platformId,
        currentBook.id,
        totalLessonsCount,
      );
      setPercentage(result.data.percentage);
      setCompletedLessons(result.data.completedLessons);
    } catch (error) {
      console.log("Error getting percentage by book: ", error);
      setPercentage(0);
      setCompletedLessons(0);
      return;
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.banner}>
      <div style={{ flexGrow: 1 }}>
        {courses.length > 1 ? (
          <div className={styles.customSelect}>
            <button
              onClick={toggleDropdown}
              className={styles.dropdownToggle}
              ref={dropdownRef}
            >
              {selectedBook}
              <ChevronDownIcon className={styles.icon} />
            </button>
            {isDropdownOpen && (
              <ul className={styles.dropdownMenu}>
                <li className={styles.firstOption}>Courses</li>
                {sortedCourses.map((course) => (
                  <li
                    key={course.book.id}
                    onClick={() => handleBookSelection(course.book)}
                    className={
                      course.book.name === selectedBook ? styles.selected : ""
                    }
                  >
                    {course.book.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <span className={styles.onlyBook}>{currentBook.name}</span>
        )}
        <div className={styles.progressBar}>
          <p>{statusBarPhrase}</p>
          <div className={styles.bar}>
            <progress max="100" value={progressValue}>
              {progressValue}%
            </progress>
          </div>
        </div>
      </div>
      <div>
        <img className={styles.illustration} src={selectCoursesIllustration} />
      </div>
    </div>
  );
};

export default Banner;
